<template>
	<WaitDialog :is-active="isWaiting" title="Loading data..." />
	<WaitDialog :is-active="isWaitingOrder" title="Ordering Violation Prediction..." />
	<v-container>
		<!-- Header Display / Button Options -->
		<v-row justify="space-between" class="justify-sm-center justify-xs-center">
			<v-col class="d-flex justify-start">
				<h3>Policy Number: {{ policyNumber }}</h3>
			</v-col>
			<v-col class="d-flex justify-end justify-xs-start">
				<v-chip v-if="isDuplicate" class="warning" size="large" color="warning" outlined label>
					<v-icon start icon="mdi-alert-box"></v-icon>
					Unit’s Assigned Driver does not match driver details on Driver’s List - please review.
				</v-chip>
			</v-col>
		</v-row>

		<v-row>
			<v-col class="d-flex justify-end">
				<MBtn color="primary" prepend-icon="mdi-card-account-details" @click="onGetViolationPrediction">Order
				</MBtn>
			</v-col>
		</v-row>

		<!-- <v-row>
			<v-col>
				<v-alert v-if="validationErrorMessages.length > 0" type="error" variant="tonal" density="compact"
					title="Errors on Driver Data" icon="mdi mdi-alert-circle-outline" class="mb-4" border prominent>
					<div v-for="(error, errorIdx) in validationErrorMessages" :key="errorIdx">
						<div v-for="(msg, i) in error.messages" :key="i" class="text-left">
							• {{ msg }}
						</div>
					</div>
				</v-alert>
			</v-col>
		</v-row> -->

		<v-container class="pa-0">
			<UserNotification v-for="(error, index) in validationErrorMessages" :key="index"
				:message="error.messages.join('\n')" type="error" />
		</v-container>

		<v-data-table :headers="headers" :items="drivers?.drivers || []" class="text-left elevation-1 mt-6"
			:row-props="getRowProps" item-key="driverId" hover color="primaryMoe" :items-per-page="itemsPerPage"
			:footer-props="{
				itemsPerPageOptions: [5, 10, 25, 50, -1],
				itemsPerPageText: 'Drivers per page:',
				showFirstLastPage: true
			}">

			<template #headers="{ columns }">
				<tr>
					<th :id="'headers-' + column.key" v-for="column in columns" :key="column.key"
						class="font-weight-bold">
						<template v-if="column.key === 'selected'">
							<!-- Show disabled state when no selectable drivers exist -->
							<template v-if="selectableDriversCount === 0">
								<v-tooltip text="No drivers available for selection - all have existing data">
									<template v-slot:activator="{ props }">
										<span v-bind="props">
											<v-checkbox :model-value="false" :disabled="true" :indeterminate="true"
												color="warning" density="compact" hide-details />
										</span>
									</template>
								</v-tooltip>
							</template>
							<!-- Normal select all behavior when selectable drivers exist -->
							<template v-else>
								<v-checkbox :model-value="selectAllState" :indeterminate="selectAllState === null"
									@update:model-value="toggleSelectAll" color="primary" density="compact"
									hide-details />
							</template>
						</template>
						<template v-else>
							{{ column.title }}
						</template>
					</th>
				</tr>
			</template>

			<!-- eslint-disable vue/valid-v-slot -->
			<template #item.selected="{ item }">
				<template v-if="item.violationPrediction != null || item.motorVehicleReport != null">
					<v-tooltip :text="getDisabledTooltipText(item)">
						<template v-slot:activator="{ props }">
							<span v-bind="props">
								<v-checkbox :model-value="false" :disabled="true" :indeterminate="true" color="warning"
									density="compact" hide-details></v-checkbox>
							</span>
						</template>
					</v-tooltip>
				</template>
				<template v-else>
					<v-checkbox v-model="item.selected" :disabled="false" :indeterminate="false" color="primary"
						density="compact" hide-details></v-checkbox>
				</template>
			</template>

			<!-- eslint-disable vue/valid-v-slot -->
			<template #item.dateOfBirth="{ item }">
				{{ formatDate(item.dateOfBirth) }}
			</template>

			<!-- eslint-disable vue/valid-v-slot -->
			<template #item.excludedCode="{ item }">
				<v-chip :color="getToggleColorError(item.excludedCode === 'Y')" size="small" rounded="pill" label>
					{{ toYesNoLabel(item.excludedCode) }}
				</v-chip>
			</template>

			<!-- eslint-disable vue/valid-v-slot -->
			<template #item.violationPrediction="{ item }">
				<span v-if="item.violationPrediction === null || item.violationPrediction === undefined"></span>
				<v-chip v-else :color="getToggleColorWarning(item.violationPrediction.orderMvr === 'Y')" size="small" rounded="pill"
					label>
					{{ item.violationPrediction.orderMvr === 'Y' ? 'Yes' : 'No' }}
				</v-chip>
			</template>

			<!-- eslint-disable vue/valid-v-slot -->
			<template #item.mvrResponse="{ item }">
				<div v-if="(item as DriverTableItem).productProcessingStatusDescription"
					class="d-flex align-center justify-space-between">
					<v-chip :color="getMvrChipColor((item as DriverTableItem).productProcessingStatusDescription!)" dark
						small>
						{{ getMvrResponse(item as DriverTableItem) }}
					</v-chip>

					<v-tooltip location="top"
						v-if="(item as DriverTableItem).productProcessingStatusDescription &&
							['Activity Found', 'Hit', 'Hit - Activity Found', 'Additional response - Hit'].includes((item as DriverTableItem).productProcessingStatusDescription!)">
						<template #activator="{ props }">
							<v-btn icon size="small" @click="openSidebar(item as DriverTableItem)" v-bind="props"
								color="primary">
								<v-icon>mdi-account-file-text</v-icon>
							</v-btn>
						</template>
						<span>View MVR Summary</span>
					</v-tooltip>
				</div>
				<span v-else></span>
			</template>

		</v-data-table>

		<!-- Sidebar for MVR Details -->
		<v-navigation-drawer v-model="sidebarOpen" location="right" temporary :width="drawerWidth">
			<MvrSummary v-if="sidebarData" :mvrData="sidebarData" />
			<template v-slot:append>
				<v-container fluid>
					<v-row justify="space-between" v-if="sidebarOpen">
						<v-col cols="auto">
							<MBtn icon="mdi-chevron-right" variant="text" @click.stop="sidebarOpen = !sidebarOpen"
								color="primaryMoe">
							</MBtn>
						</v-col>
						<v-col cols="auto">
							<v-btn prepend-icon="mdi-send" @click="sendDocument()" color="primary">
								Send to Onbase
							</v-btn>
						</v-col>
					</v-row>
				</v-container>
			</template>
		</v-navigation-drawer>
	</v-container>
</template>

<script lang="ts">
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import WaitDialog from '@/components/WaitDialog.vue';
import UserNotification from '@/components/UserNotification.vue';
import MvrSummary from '@/components/MvrSummary.vue';
import { DriverAddress, DriverDetailRequest, DriverInformation, DriverInformationList, DriverValidationError, ViolationOrderApiResponse, DriverTableItem, OrderMvrResponseBody, MvrApiMotorVehicleReport, NewApiInsuredParty } from '@/models/DriverInformationModels';
import { DataTableHeader } from '@/models/DataTableHeader';
import { getDrivers, getViolationPredictionRaw } from '@/services/DriverService';
import { TelemetryContract } from '@/contracts/TelemetryContract';
import type { Tell } from '@/services/Tell';
import { generateOrderRequestData, hasDuplicateDriverRecord, updateDriversWithViolationPrediction, validateDriverDetailRequests, validateDriversLicense, DriversLicenseValidationInput, mapMvrResponseData, updateDriversWithMvrData, mapSingleMvrReport } from '@/utils/DriverUtils';
import MBtn from '@/components/BaseButton.vue';
import { useDisplayFormatters } from '@/composables/UseDisplayFormatters';
import { useOperationMessaging } from '@/composables/UseOperationMessaging';
import { useDisplay, useTheme } from 'vuetify';
import { CommercialDataService } from '@/services/CommercialDataService';


export default {
	name: 'ViewDriver',
	route: {
		path: '/drivers',
		meta: {
			requiresAuth: true,
			title: 'Driver Information'
		}
	},
	components: {
		WaitDialog,
		MBtn,
		UserNotification,
		MvrSummary
	},
	setup() {
		const route = useRoute();
		const policyNumber = ref<string | null>(
			(route.query.policyNumber as string) || null
		);

		const drivers = ref<DriverInformationList | null>(null);
		const insuredPartys = ref<NewApiInsuredParty[]>([]);
		const orderViolationPrediction = ref<DriverDetailRequest[] | null>(null);
		const violationOrderResponse = ref<ViolationOrderApiResponse | null>(null);
		const validationErrorMessages = ref<DriverValidationError[]>([]);

		const address = ref<DriverAddress>(null);
		const persist = ref<boolean>(true);
		const isDuplicate = ref<boolean>(false);
		const itemsPerPage = ref<number>(10); // Default to 10 items per page
		const { withMessaging } = useOperationMessaging();
		const display = useDisplay();
		const { width, xs, sm, md } = display;
		const theme = useTheme();
		const themeIsDark = ref<boolean>(false);

		const drawerWidth = computed(() => {
			if (xs.value) return Math.min(width.value * 0.95, 500); // 95% on extra small screens
			if (sm.value) return Math.min(width.value * 0.85, 700); // 85% on small screens
			if (md.value) return Math.min(width.value * 0.75, 900); // 75% on medium screens
			return Math.min(width.value * 0.6, 1100); // 60% on large screens, max 1000px
		});

		// MVR Sidebar state
		const sidebarOpen = ref(false);
		const sidebarData = ref<OrderMvrResponseBody | null>(null);

		const invalidDriverIds = computed(() =>
			validationErrorMessages.value.map(e => e.driverId)
		);

		// Computed property for select all checkbox
		const selectAllState = computed(() => {
			if (!drivers.value?.drivers) return false;

			const selectableDrivers = drivers.value.drivers.filter(driver => driver.violationPrediction == null && driver.motorVehicleReport == null);
			if (selectableDrivers.length === 0) return false;

			const selectedCount = selectableDrivers.filter(driver => driver.selected).length;

			if (selectedCount === 0) return false;
			if (selectedCount === selectableDrivers.length) return true;
			return null; // indeterminate state
		});

		const selectableDriversCount = computed(() => {
			if (!drivers.value?.drivers) return 0;

			return drivers.value.drivers.filter(driver =>
				driver.violationPrediction == null && driver.motorVehicleReport == null
			).length;
		});

		const getDisabledTooltipText = (item: DriverInformation): string => {
			if (item.violationPrediction != null && item.motorVehicleReport != null) {
				return 'Driver cannot be selected - both Violation Prediction and MVR data are present';
			}
			if (item.violationPrediction != null) {
				return 'Driver cannot be selected - Violation Prediction is present';
			}
			if (item.motorVehicleReport != null) {
				return 'Driver cannot be selected - MVR data is present';
			}
			return 'Driver cannot be selected';
		};

		const toggleSelectAll = () => {
			if (!drivers.value?.drivers) return;

			const selectableDrivers = drivers.value.drivers.filter(driver => driver.violationPrediction == null && driver.motorVehicleReport == null);
			const allSelected = selectableDrivers.every(driver => driver.selected);

			selectableDrivers.forEach(driver => {
				driver.selected = !allSelected;
			});
		};

		const isLocallyBusy = ref(true);
		const isWaiting = computed(() => isLocallyBusy.value);
		const isWaitingOrder = ref(false);
		const { getToggleColorWarning, getToggleColorError, formatDate, toYesNoLabel, toYesNoLabelBoolean } = useDisplayFormatters();

		const tell = inject('tell') as Tell;
		const telemetry = inject('telemetry') as TelemetryContract;

		const headers = ref<DataTableHeader[]>([
			{ title: 'Select', key: 'selected', align: 'center', sortable: false },
			{ title: 'First Name', key: 'firstName', align: 'start' },
			{ title: 'M.I.', key: 'middleInitial', align: 'start' },
			{ title: 'Last Name', key: 'lastName', align: 'start' },
			{ title: 'DOB', key: 'dateOfBirth', align: 'start' },
			{ title: 'License State', key: 'licenseState', align: 'start' },
			{ title: 'License Number', key: 'licenseNumber', align: 'start' },
			{ title: 'Assigned Unit', key: 'assignedUnit', align: 'start' },
			{ title: 'Excluded Driver', key: 'excludedCode', align: 'start' },
			{ title: 'Violation Predictor', key: 'violationPrediction', align: 'start' },
			{ title: 'MVR Response', key: 'mvrResponse', align: 'start' }
		]);

		onMounted(async () => {
			const { drivers: driverList, address: driverAddress, insuredPartys: insuredParties } = await getDrivers(policyNumber, tell, telemetry);
			drivers.value = driverList;
			address.value = driverAddress;
			insuredPartys.value = insuredParties || [];
			
			console.log('Loaded drivers:', drivers.value);

			if (drivers.value?.drivers) {
				// First, collect existing MVR data to process
				const existingMvrData: OrderMvrResponseBody[] = [];
				
				for (const driver of drivers.value.drivers) {

					const validationInput: DriversLicenseValidationInput = {
						state: driver.licenseState,
						licenseNumber: driver.licenseNumber,
						firstName: driver.firstName,
						middleInitial: driver.middleInitial,
						lastName: driver.lastName,
						dateOfBirth: driver.dateOfBirth
					};

					const validationResult = await validateDriversLicense(validationInput);

					// If validation result contains errors, push them to validationErrorMessages
					if (validationResult && validationResult.length > 0) {
						validationErrorMessages.value.push({
							driverId: driver.driverId,
							messages: validationResult
						});
					}
					
					// Process existing MVR data if it exists
					if (driver.motorVehicleReport) {
						const mappedMvrData = mapSingleMvrReport(driver.motorVehicleReport as MvrApiMotorVehicleReport);
						existingMvrData.push(mappedMvrData);
					}
				}
				
				// Update drivers with processed MVR data to ensure UI properties are set
				if (existingMvrData.length > 0) {
					drivers.value = updateDriversWithMvrData(existingMvrData, drivers.value);
					
					// Also store the processed MVR data back to motorVehicleReport for sidebar access
					drivers.value.drivers.forEach(driver => {
						const matchingMvrData = existingMvrData.find(mvr => 
							mvr.driverInfo.licenseNumber === driver.licenseNumber && 
							mvr.driverInfo.licenseState === driver.licenseState
						);
						if (matchingMvrData) {
							driver.motorVehicleReport = matchingMvrData;
						}
					});
				}
			}

			// Initialize selected property for each driver
			if (drivers.value?.drivers) {
				drivers.value.drivers.forEach(driver => {
					if (driver.selected === undefined) {
						driver.selected = false;
					}
				});
			}

			isDuplicate.value = drivers.value ? hasDuplicateDriverRecord(drivers.value) : false;
			isLocallyBusy.value = false;
			orderViolationPrediction.value = generateOrderRequestData(drivers.value, address.value);

			// Append additional validation errors instead of replacing
			const additionalErrors = validateDriverDetailRequests(orderViolationPrediction.value);
			validationErrorMessages.value.push(...additionalErrors);
		});

		const onGetViolationPrediction = async () => {
			orderViolationPrediction.value = generateOrderRequestData(drivers.value, address.value);

			if (!orderViolationPrediction.value) return;

			// Get the driver IDs that will be included in the order (selected drivers without predictions)
			const selectedDriversForOrder = drivers.value?.drivers?.filter(
				driver => driver.selected && driver.violationPrediction == null
			) || [];

			if (selectedDriversForOrder.length === 0) {
				tell.info('No drivers selected for violation prediction order.');
				return;
			}

			// Only check validation errors for drivers that are selected for ordering
			const selectedDriverIds = selectedDriversForOrder.map(driver => driver.driverId);
			const relevantValidationErrors = validationErrorMessages.value.filter(
				error => selectedDriverIds.includes(error.driverId)
			);

			if (relevantValidationErrors.length > 0) {
				const errorMsg = 'Please resolve the errors for selected drivers before ordering violation prediction.';
				const hasErrorMsg = validationErrorMessages.value.some(
					(err) => Array.isArray(err.messages) && err.messages.includes(errorMsg)
				);
				if (!hasErrorMsg) {
					validationErrorMessages.value.push({ driverId: '', messages: [errorMsg] });
				}
				return;
			}

			if (orderViolationPrediction.value.length === 0) {
				tell.info('Violation Predictor present on all selected drivers. No order needed.');
				return;
			}

			isWaitingOrder.value = true;

			await withMessaging('Order Violation Prediction', async () => {
				try {
					violationOrderResponse.value = await getViolationPredictionRaw(
						policyNumber,
						orderViolationPrediction.value,
						persist.value
					);
					drivers.value = updateDriversWithViolationPrediction(violationOrderResponse.value, drivers.value);
					// Process MVR data and store it in driver objects
					const newMvrData = mapMvrResponseData(violationOrderResponse.value);
					
					// Update drivers with new MVR data 
					drivers.value = updateDriversWithMvrData(newMvrData, drivers.value);
					
					// Also store the processed MVR data in the motorVehicleReport property for sidebar access
					drivers.value.drivers.forEach(driver => {
						const matchingMvrData = newMvrData.find(mvr => 
							mvr.driverInfo.licenseNumber === driver.licenseNumber && 
							mvr.driverInfo.licenseState === driver.licenseState
						);
						if (matchingMvrData) {
							driver.motorVehicleReport = matchingMvrData;
						}
					});
				} finally {
					isWaitingOrder.value = false;
				}
			});

			if (violationOrderResponse.value?.errors?.length > 0) {
				const uniqueErrors = [...new Set(violationOrderResponse.value.errors)];
				tell.error(` ${uniqueErrors.join('; ')}`);
				return;
			}

		};

		const getRowProps = ({ item }: { item: DriverInformation }) => {
			return {
				class: invalidDriverIds.value.includes(item.driverId) ? 'row-error' : '',
			};
		};

		const openSidebar = (driver: DriverInformation) => {
			// Use the driver's motorVehicleReport directly - it's already in the correct format
			if (driver.motorVehicleReport) {
				sidebarData.value = driver.motorVehicleReport;
			} else {
				sidebarData.value = null;
			}
			sidebarOpen.value = true;
		};

		const sendDocument = async () => {
			if (theme.current.value.dark === true) {
				theme.global.name.value = 'light';
				//Boolean value used to determine if the user previously had their theme set to dark
				themeIsDark.value = true;
			}

			// Get insured party full name (business/entity name)
			const insuredPartyFullName = insuredPartys.value?.[0]?.fullName || undefined;

			await CommercialDataService.exportMvrDocument(sidebarData.value, policyNumber.value, insuredPartyFullName);

			//Value will only be true if user tries exporting document when they have dark mode
			if (themeIsDark.value === true) {
				theme.global.name.value = 'dark';
			}
		};

		const getMvrChipColor = (status: string) => {
			const red = ['Activity Found', 'Hit - Activity Found', 'Additional response - Hit'];
			const yellow = ['Alert Message', 'Invalid Order', 'Pending Information', 'Soundex Rejected', 'Unreturned'];
			const green = ['Clear', 'Activity File Clear', 'Additional response - Clear', 'No Hit'];

			if (red.includes(status)) return 'error';
			if (yellow.includes(status)) return 'warning';
			if (green.includes(status)) return 'success';
			return 'surface-variant';
		};

		const getMvrResponse = (item: DriverTableItem) => {
			const status = item.productProcessingStatusDescription || '';
			const recordType = item.recordType || '';
			const hitStatuses = ['Activity Found', 'Hit - Activity Found', 'Additional response - Hit'];
			return hitStatuses.includes(status) && recordType ? `${status} - ${recordType}` : status;
		};

		return {
			isWaiting,
			isWaitingOrder,
			drivers,
			headers,
			policyNumber,
			isDuplicate,
			itemsPerPage,
			validationErrorMessages,
			getToggleColorWarning,
			getToggleColorError,
			toYesNoLabel,
			toYesNoLabelBoolean,
			formatDate,
			onGetViolationPrediction,
			getRowProps,
			selectAllState,
			toggleSelectAll,
			sidebarOpen,
			sidebarData,
			openSidebar,
			sendDocument,
			getMvrChipColor,
			getMvrResponse,
			drawerWidth,
			getDisabledTooltipText,
			selectableDriversCount
		};
	}
};
</script>

<style scoped>
:deep(.v-field-label) {
	font-size: 0.75rem !important;
}

:deep(.v-field__input) {
	font-size: 0.75rem !important;
	text-align: center;
}

.v-field-label {
	font-size: 0.75rem !important;
}

::v-deep(.row-error) {
	background-color: rgba(var(--v-theme-error), 0.1) !important;
}
</style>
